import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import GoogleAuth from './GoogleAuth';
import logger from '../utils/logger';
import { adminFetchInit } from '../utils/adminToken';
import 'flag-icons/css/flag-icons.min.css';

function Header({ streamer = null }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Removed firstAmbassador state - no longer needed since we use absolute paths

  // Helper function to get website based on streamer
  const getWebsite = useCallback(() => {
    if (!streamer) return null;
    // Convert streamer name to website format (e.g., 'bob' -> 'bob.com')
    return `${streamer}.com`;
  }, [streamer]);
  
  // Removed fetchFirstAmbassador function and useEffect - no longer needed
  
  // Detect if we're on a streamer page to make navigation links dynamic
  const pathname = location.pathname;
  
  // Determine if this is a main site page or streamer page
  let isMainSitePage = false;
  
  // Handle root path
  if (pathname === '/') {
    isMainSitePage = true;
  } else {
    // Extract the first segment after the leading slash
    const pathSegments = pathname.split('/').filter(segment => segment.length > 0);
    const firstSegment = pathSegments[0];
    
    // Define main site pages that should not be treated as streamer pages
    const mainSitePages = ['submit', 'gallery', 'shop', 'application', 'login', 'terms', 'legal-notices', 'admin', 'user', 'streamer'];
    
    if (mainSitePages.includes(firstSegment)) {
      isMainSitePage = true;
    } else {
      // This is a streamer page
      isMainSitePage = false;
    }
  }
  
  // Get the base path for navigation links  
  const basePath = !isMainSitePage && streamer ? `/${streamer}` : '';
  
  // Navigation now uses absolute paths for Submit and Voter links
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [expiringChallenge, setExpiringChallenge] = useState(null);
  const [allActiveChallenges, setAllActiveChallenges] = useState([]);
  const [showChallengesDropdown, setShowChallengesDropdown] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  
  // Language dropdown state
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  
  // Drop Zone dropdown state
  const [showDropZoneDropdown, setShowDropZoneDropdown] = useState(false);
  const [streamers, setStreamers] = useState([]);
  const [loadingStreamers, setLoadingStreamers] = useState(false);
  const [streamerSearchQuery, setStreamerSearchQuery] = useState('');
  const dropZoneDropdownRef = useRef(null);
  const dropZoneDropdownRefMobile = useRef(null);
  const [showMobileDropZones, setShowMobileDropZones] = useState(false); // Toggle between menu and drop zones
  
  // Sync Drop Zones state between mobile and desktop
  useEffect(() => {
    // Mobile Drop Zones active → activate desktop dropdown
    if (showMobileDropZones) {
      setShowDropZoneDropdown(true);
    }
  }, [showMobileDropZones]);
  
  useEffect(() => {
    // Desktop dropdown active → activate mobile Drop Zones view
    if (showDropZoneDropdown) {
      setShowMobileDropZones(true);
    } else {
      // Desktop dropdown closed → close mobile Drop Zones view
      setShowMobileDropZones(false);
    }
  }, [showDropZoneDropdown]);
  
  // Admin check state
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Available languages
  const languages = [
    { code: 'fr', name: 'Français', flag: 'fi fi-fr' },
    { code: 'en', name: 'English', flag: 'fi fi-us' }
  ];
  const [challengeForm, setChallengeForm] = useState({
    name: '',
    startDate: '',
    endDate: '',
    thematic: 'Food',
    description: '',
    rewards: {
      first: ['', ''],
      second: ['', ''],
      third: ['', '']
    }
  });
  const [showColorPalette, setShowColorPalette] = useState(false);
  const { user, logout, isAuthenticated, googleLoginLoading } = useAuth();
  const { currentLanguage, changeLanguage, t } = useLanguage();
  const userMenuRef = useRef(null);
  const modalRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const colorPickerRef = useRef(null);
  const challengesDropdownRef = useRef(null);
  const languageDropdownRef = useRef(null);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    // Reset Drop Zones view when closing menu (only if dropdown is also closed)
    if (mobileMenuOpen && !showDropZoneDropdown) {
      setShowMobileDropZones(false);
    }
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
  };

  // Language selection handler
  const handleLanguageSelect = (languageCode) => {
    changeLanguage(languageCode);
    setShowLanguageDropdown(false);
    console.log('Language selected:', languageCode);
  };

  // Get current language data
  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === currentLanguage) || languages[1]; // Default to English (index 1)
  };

  const openChallengeModal = () => {
    setShowChallengeModal(true);
    setUserMenuOpen(false); // Close the user menu when opening the modal
  };

  const closeChallengeModal = () => {
    setShowChallengeModal(false);
  };

  const handleChallengeFormChange = (e) => {
    const { name, value } = e.target;
    setChallengeForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRewardChange = (position, index, value) => {
    setChallengeForm(prev => ({
      ...prev,
      rewards: {
        ...prev.rewards,
        [position]: prev.rewards[position].map((reward, i) => 
          i === index ? value : reward
        )
      }
    }));
  };

  const addReward = (position) => {
    setChallengeForm(prev => ({
      ...prev,
      rewards: {
        ...prev.rewards,
        [position]: [...prev.rewards[position], '']
      }
    }));
  };

  const removeReward = (position, index) => {
    setChallengeForm(prev => ({
      ...prev,
      rewards: {
        ...prev.rewards,
        [position]: prev.rewards[position].filter((_, i) => i !== index)
      }
    }));
  };

  const handleChallengeSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    // Check if user has admin role
    if (user?.role?.toLowerCase() !== 'admin') {
      setSubmitError('Only administrators can create challenges.');
      setIsSubmitting(false);
      return;
    }

    // Validate form
    if (!challengeForm.name.trim()) {
      setSubmitError('Challenge name is required');
      setIsSubmitting(false);
      return;
    }
    if (!challengeForm.startDate) {
      setSubmitError('Start date is required');
      setIsSubmitting(false);
      return;
    }
    if (!challengeForm.endDate) {
      setSubmitError('End date is required');
      setIsSubmitting(false);
      return;
    }
    if (new Date(challengeForm.startDate) >= new Date(challengeForm.endDate)) {
      setSubmitError('End date must be after start date');
      setIsSubmitting(false);
      return;
    }
    if (!challengeForm.description.trim()) {
      setSubmitError('Description is required');
      setIsSubmitting(false);
      return;
    }

    try {
      // Prepare rewards data - filter out empty rewards
      const preparedRewards = {
        first: challengeForm.rewards.first.filter(reward => reward.trim() !== ''),
        second: challengeForm.rewards.second.filter(reward => reward.trim() !== ''),
        third: challengeForm.rewards.third.filter(reward => reward.trim() !== '')
      };

      // Format the data to match our Supabase table structure
      const challengeData = {
        title: challengeForm.name.trim(),
        description: challengeForm.description.trim(),
        color: '#06b6d4', // Default cyan color
        expiration: challengeForm.endDate,
        startDate: challengeForm.startDate,
        thematic: challengeForm.thematic,
        rewards: preparedRewards,
        userId: user?.id || user?.sub || user?.email || 'anonymous'
      };

      console.log('Creating challenge with user data:', { 
        userId: challengeData.userId, 
        userObject: user 
      });

      // Call our serverless function to save data
      const response = await fetch('/.netlify/functions/challenge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(challengeData)
      });

      if (!response.ok) {
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create challenge');
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
          throw new Error(`Failed to create challenge: ${response.status} ${response.statusText}`);
        }
      }

      const result = await response.json();
      console.log('Challenge created successfully:', result);
      
      // Reset form and show success message
      setChallengeForm({
        name: '',
        startDate: '',
        endDate: '',
        thematic: 'Food',
        description: '',
        rewards: {
          first: ['', ''],
          second: ['', ''],
          third: ['', '']
        }
      });
      
      // Set success state and message
      setSuccessMessage('Challenge created successfully!');
      setSubmitSuccess(true);
      setShowChallengeModal(false);
      
      // Automatically hide success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
        setSuccessMessage('');
      }, 5000);
    } catch (error) {
      console.error('Error creating challenge:', error);
      setSubmitError(error.message || 'Failed to create challenge. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close user menu and modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      
      if (challengesDropdownRef.current && !challengesDropdownRef.current.contains(event.target)) {
        setShowChallengesDropdown(false);
      }
      
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
      
      if (dropZoneDropdownRef.current && !dropZoneDropdownRef.current.contains(event.target)) {
        // Also check mobile ref
        if (dropZoneDropdownRefMobile.current && dropZoneDropdownRefMobile.current.contains(event.target)) {
          return; // Don't close if clicking inside mobile dropdown
        }
        setShowDropZoneDropdown(false);
      }
      
      if (modalRef.current && !modalRef.current.contains(event.target) && showChallengeModal) {
        closeChallengeModal();
      }
      
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target) && showColorPalette) {
        setShowColorPalette(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen, showChallengeModal, showColorPalette]);

  // Handle escape key for closing the modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showChallengeModal) {
        setShowChallengeModal(false);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showChallengeModal]);

  // Fetch the nearest expiring challenge and all active challenges
  useEffect(() => {
    const fetchExpiringChallenge = async () => {
      try {
        logger.log('🏁 [HEADER] Fetching expiring challenge...');
        
        // Build URL with website filtering
        let url = '/.netlify/functions/expiring-challenge';
        const website = getWebsite();
        if (website) {
          url += `?website=${website}`;
          logger.log(`🌐 [HEADER] Fetching challenges for website: ${website}`);
        }
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        logger.log('🏁 [HEADER] Expiring challenge response:', data);
        
        if (data.success) {
          // Store the main challenge (nearest expiring)
          if (data.data) {
            logger.log('✅ [HEADER] Found expiring challenge:', data.data.title, 'expires:', data.data.expiration);
            // Force new render with a new object
            setExpiringChallenge({...data.data});
            
            // Reset timeRemaining to trigger calculation
            setTimeRemaining({
              days: 0,
              hours: 0,
              minutes: 0,
              seconds: 0
            });
          } else {
            logger.log('📝 [HEADER] No active challenges found');
            setExpiringChallenge(null);
          }
          
          // Store all active challenges for the dropdown
          if (data.allActiveChallenges && Array.isArray(data.allActiveChallenges)) {
            logger.log(`📋 [HEADER] Found ${data.allActiveChallenges.length} active challenges`);
            setAllActiveChallenges(data.allActiveChallenges);
          } else {
            logger.log('📋 [HEADER] No active challenges array found');
            setAllActiveChallenges([]);
          }
        } else {
          logger.error('❌ [HEADER] API returned success: false', data);
          setExpiringChallenge(null);
          setAllActiveChallenges([]);
        }
      } catch (error) {
        logger.error('❌ [HEADER] Error fetching expiring challenge:', error);
        setExpiringChallenge(null);
        setAllActiveChallenges([]);
      }
    };
    
    // Initial fetch
    fetchExpiringChallenge();
    
    // Refresh every 5 minutes to catch newly active challenges
    const refreshInterval = setInterval(fetchExpiringChallenge, 5 * 60 * 1000);
    
    return () => {
      clearInterval(refreshInterval);
    };
  }, [getWebsite]);
  
  // Initialize timeRemaining with default values
  useEffect(() => {
    if (!timeRemaining) {
      setTimeRemaining({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if user is an admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!isAuthenticated || !user || !user.email) {
        setIsAdmin(false);
        return;
      }

      try {
        const response = await fetch(
          `/.netlify/functions/check-admin?email=${encodeURIComponent(user.email)}`,
          adminFetchInit()
        );
        if (response.ok) {
          const data = await response.json();
          setIsAdmin(data.isAdmin || false);
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, [isAuthenticated, user]);

  // Fetch streamers when Drop Zone dropdown is opened
  useEffect(() => {
    if (showDropZoneDropdown && streamers.length === 0) {
      const fetchStreamers = async () => {
        try {
          setLoadingStreamers(true);
          const response = await fetch('/.netlify/functions/get-admin-ambassadors');
          const result = await response.json();
          
          console.log('Drop Zone streamers response:', result);
          
          if (result.success && result.data) {
            console.log('Streamers data:', result.data);
            console.log('First streamer:', result.data[0]);
            setStreamers(result.data);
          }
        } catch (error) {
          console.error('Error fetching streamers:', error);
        } finally {
          setLoadingStreamers(false);
        }
      };
      
      fetchStreamers();
    }
  }, [showDropZoneDropdown, streamers.length]);

  // Update the timer countdown
  useEffect(() => {
    
    
    const calculateTimeRemaining = () => {
      if (!expiringChallenge) {
        
        return;
      }
      
      
      
      // Parse the expiration string using explicit UTC parsing
      const expirationStr = expiringChallenge.expiration;
      const expYear = parseInt(expirationStr.substring(0, 4));
      const expMonth = parseInt(expirationStr.substring(5, 7)) - 1; // JS months are 0-indexed
      const expDay = parseInt(expirationStr.substring(8, 10));
      const expHour = parseInt(expirationStr.substring(11, 13) || '0');
      const expMinute = parseInt(expirationStr.substring(14, 16) || '0');
      const expSecond = parseInt(expirationStr.substring(17, 19) || '0');
      
      // Create UTC date
      const expirationDate = new Date(Date.UTC(expYear, expMonth, expDay, expHour, expMinute, expSecond));
      
      
      const now = new Date();
      const difference = expirationDate.getTime() - now.getTime();
      
      
      if (difference <= 0) {
        // Challenge has expired
        
        setTimeRemaining({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        });
        setExpiringChallenge(null);
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        return;
      }
      
      // Calculate remaining time
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      
      setTimeRemaining({ days, hours, minutes, seconds });
      
    };
    
    if (expiringChallenge) {
      // Calculate immediately
      calculateTimeRemaining();
      
      // Then set up interval
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      timerIntervalRef.current = setInterval(calculateTimeRemaining, 1000);
    }
    
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [expiringChallenge]);
  
  // Handle window resize to close mobile menu when resized to desktop
  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector('header');
      if (header) {
        if (window.scrollY > 50) {
          header.classList.add('header-sticky-active');
        } else {
          header.classList.remove('header-sticky-active');
        }
      }
    };
    
    const handleResize = () => {
      // Check if we're at a desktop breakpoint (md in Tailwind is 768px)
      if (window.innerWidth >= 768 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    // Initial check for screen size
    handleResize();
    
    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [mobileMenuOpen]);

  
  return (
    <>
    {/* Challenge Timer Banner at the very top */}
    {expiringChallenge && (
      <div className="relative">
        <div 
          className="bg-black w-full py-2 text-center text-white fixed top-0 left-0 right-0 z-[60] hover:bg-gray-900 transition-colors duration-200"
        >
          <div className="container mx-auto">
            <div className="flex flex-row items-center justify-center cursor-pointer"
              onClick={() => {
                
                const isAlreadyOnSubmitPage = window.location.pathname === '/submit';
                
                if (isAlreadyOnSubmitPage) {
                  // If already on submit page, use window.location to force a full page refresh
                  window.location.href = `/submit?challenge=${expiringChallenge.id}`;
                } else {
                  // Normal navigation when coming from another page
                  navigate(`/submit?challenge=${expiringChallenge.id}`);
                }
              }}
              title="Click to submit for this challenge"
            >
              <span className="mr-2 font-bold uppercase">{expiringChallenge.title}</span>
              <span className="uppercase">Se termine dans : </span>
              <div className="ml-2 flex space-x-2">
                <div className="flex items-center">
                  <span className="font-mono font-bold">{timeRemaining.days}</span>
                  <span className="text-xs ml-1">Day</span>
                </div>
                <div className="flex items-center">
                  <span className="font-mono font-bold">{timeRemaining.hours}</span>
                  <span className="text-xs ml-1">H</span>
                </div>
                <div className="flex items-center">
                  <span className="font-mono font-bold">{timeRemaining.minutes}</span>
                  <span className="text-xs ml-1">M</span>
                </div>
                <div className="flex items-center">
                  <span className="font-mono font-bold">{timeRemaining.seconds}</span>
                  <span className="text-xs ml-1">S</span>
                </div>
              </div>
            </div>
            
            {/* Red arrow dropdown button */}
            <div 
              className="absolute right-5 top-0 bottom-0 flex items-center justify-center cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setShowChallengesDropdown(!showChallengesDropdown);
              }}
              title="Show all active challenges"
            >
              <div className="flex items-center text-pink-500 hover:text-pink-400 transition-colors duration-200">
                <span className="mr-1 text-sm font-medium">Challenges</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  {showChallengesDropdown ? (
                    <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                  ) : (
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  )}
                </svg>
              </div>
            </div>
            
            {/* Challenges dropdown */}
            {showChallengesDropdown && (
              <div 
                className="absolute right-5 top-10 z-[70] w-64 bg-black border border-gray-700 rounded-md shadow-lg overflow-hidden"
                ref={challengesDropdownRef}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="py-1 max-h-64 overflow-y-auto">
                  <div className="px-4 py-2 text-sm text-gray-400 border-b border-gray-700">
                    All Active Challenges
                  </div>
                  {allActiveChallenges.length > 0 ? (
                    allActiveChallenges.map((challenge) => (
                      <div 
                        key={challenge.id}
                        className="px-4 py-2 text-white hover:bg-gray-800 cursor-pointer transition-colors duration-150 flex justify-between items-center"
                        onClick={() => {
                          const submitPath = `${basePath}/submit`;
                          const isAlreadyOnSubmitPage = window.location.pathname === submitPath;
                          if (isAlreadyOnSubmitPage) {
                            window.location.href = `${submitPath}?challenge=${challenge.id}`;
                          } else {
                            navigate(`${submitPath}?challenge=${challenge.id}`);
                          }
                          setShowChallengesDropdown(false);
                        }}
                      >
                        <span className="font-medium">{challenge.title}</span>
                        {challenge.id === expiringChallenge.id && (
                          <span className="text-xs text-green-400 ml-2">Current</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-gray-400 italic">
                      No active challenges found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )}
    <header className="bg-[#121214] fixed w-full z-50 transition-all duration-300" style={{marginTop: expiringChallenge ? '40px' : '0px'}}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          <a href="/" className="flex items-center">
            <img 
              src="https://res.cloudinary.com/dez2cotie/image/upload/v1748665915/Logo_PimpMyDrop_wqgnao.webp" 
              alt="Pimp My Drop Logo" 
              className="h-[6rem]" 
            />
          </a>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="/submit" className="hover:text-yellow-400 text-white font-medium">{t('submit')}</a>
            <a href="/gallery" className="hover:text-yellow-400 text-white font-medium">{t('vote')}</a>
            <a href="/shop" className="hover:text-yellow-400 text-white font-medium">{t('shop')}</a>
            
            {/* Drop Zone Dropdown */}
            <div className="relative" ref={dropZoneDropdownRef}>
              <button
                onClick={() => setShowDropZoneDropdown(!showDropZoneDropdown)}
                className="bg-cyan-600 hover:bg-cyan-700 text-white font-medium px-4 py-2 rounded-lg transition-all duration-300"
              >
                {t('dropZone')}
              </button>
              
              {/* Drop Zone Dropdown Panel */}
              {showDropZoneDropdown && (
                <div 
                  className="absolute top-full right-0 mt-2 w-80 bg-[#1A1A1A] rounded-lg shadow-xl z-50 border border-gray-600 max-h-96 overflow-y-auto"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="p-4">
                    {/* Search Field */}
                    <input
                      type="text"
                      placeholder={t('searchStreamers')}
                      value={streamerSearchQuery}
                      onChange={(e) => setStreamerSearchQuery(e.target.value)}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 mb-4"
                    />
                    
                    {/* Suggestions Label */}
                    <div className="text-gray-400 text-sm font-medium mb-3">{t('suggestions')}</div>
                    
                    {/* Streamers Grid */}
                    {loadingStreamers ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-cyan-500 border-t-transparent"></div>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-4 gap-4">
                          {streamers
                            .filter(streamer => {
                              if (!streamerSearchQuery.trim()) return true; // Show all if search is empty
                              const searchLower = streamerSearchQuery.toLowerCase().trim();
                              const ownerMatch = streamer.owner?.toLowerCase().includes(searchLower);
                              const nameMatch = streamer.name?.toLowerCase().includes(searchLower);
                              return ownerMatch || nameMatch;
                            })
                            .map((streamer) => {
                              // Get image using same logic as Home.js
                              const defaultImage = 'https://res.cloudinary.com/dez2cotie/image/upload/v1751233643/pngegg_pk3dvl.png';
                              let imageUrl = defaultImage;
                              
                              try {
                                if (streamer.image) {
                                  // Check if it's a JSON string with logo/profile structure
                                  if (typeof streamer.image === 'string' && streamer.image.startsWith('{')) {
                                    const imageData = JSON.parse(streamer.image);
                                    if (imageData.logo && Array.isArray(imageData.logo) && imageData.logo.length > 0) {
                                      imageUrl = imageData.logo[0];
                                    } else if (imageData.profile && Array.isArray(imageData.profile) && imageData.profile.length > 0) {
                                      imageUrl = imageData.profile[0];
                                    }
                                  }
                                  // Check if it's a JSON array string
                                  else if (typeof streamer.image === 'string' && streamer.image.startsWith('[')) {
                                    const imageUrls = JSON.parse(streamer.image);
                                    if (imageUrls.length > 0) {
                                      imageUrl = imageUrls[0];
                                    }
                                  }
                                  // Check if it's already an array
                                  else if (Array.isArray(streamer.image) && streamer.image.length > 0) {
                                    imageUrl = streamer.image[0];
                                  }
                                  // Single image string
                                  else if (typeof streamer.image === 'string' && streamer.image.length > 0) {
                                    imageUrl = streamer.image;
                                  }
                                }
                              } catch (error) {
                                console.error('Error parsing streamer image:', error, streamer);
                              }
                              
                              return (
                                <a
                                  key={streamer.id}
                                  href={`/${streamer.owner}`}
                                  className="flex flex-col items-center group"
                                  onClick={() => setShowDropZoneDropdown(false)}
                                >
                                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-gray-600 group-hover:border-cyan-500 transition-colors">
                                    <img
                                      src={imageUrl}
                                      alt={streamer.name || streamer.owner}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.src = defaultImage;
                                      }}
                                    />
                                  </div>
                                  <span className="text-xs text-gray-300 mt-1 text-center group-hover:text-cyan-400 transition-colors truncate w-full">
                                    {streamer.owner || streamer.name}
                                  </span>
                                </a>
                              );
                            })}
                        </div>
                        
                        {/* No results message */}
                        {streamers.filter(s => {
                          if (!streamerSearchQuery.trim()) return true;
                          const searchLower = streamerSearchQuery.toLowerCase().trim();
                          return s.owner?.toLowerCase().includes(searchLower) || s.name?.toLowerCase().includes(searchLower);
                        }).length === 0 && (
                          <div className="text-center py-8 text-gray-400">
                            No streamers found for "{streamerSearchQuery}"
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <a href="/application" className="bg-gradient-to-r from-pink-600 to-cyan-600 hover:from-pink-700 hover:to-cyan-700 text-white font-medium px-4 py-2 rounded-lg transition-all duration-300 transform hover:scale-105">{t('streamers')}</a>
            
            {/* Language Dropdown */}
            <div className="relative" ref={languageDropdownRef}>
              <button
                onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                className="flex items-center space-x-2 hover:text-yellow-400 text-white font-medium transition-colors duration-300"
              >
                <span 
                  className={`${getCurrentLanguage().flag}`}
                  style={{
                    fontSize: '16px',
                    width: '20px',
                    height: '15px',
                    borderRadius: '3px',
                    display: 'inline-block'
                  }}
                  role="img" 
                  aria-label={`${getCurrentLanguage().name} flag`}
                />
                <svg 
                  className={`h-4 w-4 transition-transform duration-200 ${showLanguageDropdown ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Language Dropdown Menu */}
              {showLanguageDropdown && (
                <div className="absolute top-full right-0 mt-2 w-40 bg-[#1A1A1A] rounded-lg shadow-xl z-50 border border-gray-600">
                  <div className="py-2">
                    {languages.map((language) => (
                      <button
                        key={language.code}
                        onClick={() => handleLanguageSelect(language.code)}
                        className={`w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-gray-700 transition-colors ${
                          currentLanguage === language.code ? 'bg-gray-700 text-yellow-400' : 'text-white'
                        }`}
                      >
                        <span 
                          className={language.flag}
                          style={{
                            fontSize: '16px',
                            width: '20px',
                            height: '15px',
                            borderRadius: '3px',
                            display: 'inline-block'
                          }}
                          role="img" 
                          aria-label={`${language.name} flag`}
                        />
                        <span className="text-sm font-medium">{language.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* User Avatar/Login Button */}
            <div className="relative ml-2" ref={userMenuRef}>
              {isAuthenticated ? (
                <div>
                  <button
                    onClick={toggleUserMenu}
                    className="flex items-center focus:outline-none"
                  >
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-[3rem] h-[3rem] rounded-full"
                    />
                  </button>
                  
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-[#1a1a1c] border border-gray-700 rounded-md shadow-2xl overflow-hidden z-50 animate-fadeIn">
                      {/* Header Section */}
                      <div className="px-4 py-3 border-b border-gray-700 bg-[#0f0f10]">
                        <p className="text-sm text-white font-semibold truncate">{user.name}</p>
                        <p className="text-xs text-gray-400 truncate mt-1">{user.email}</p>
                      </div>

                      {/* Menu Items */}
                      <div className="py-1">
                        {/* Only show CHALLENGE button for admin users */}
                        {user?.role?.toLowerCase() === 'admin' && (
                          <button 
                              onClick={openChallengeModal}
                              className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-white hover:bg-[#2a2a2c] transition-colors duration-150 group"
                            >
                              <svg className="w-5 h-5 mr-3 text-gray-400 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                              </svg>
                              <span className="tracking-wide">CHALLENGE</span>
                          </button>
                        )}

                        {/* MY PROFILE Button */}
                        <button
                          onClick={() => navigate('/user/dashboard')}
                          className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-white hover:bg-[#2a2a2c] transition-colors duration-150 group"
                        >
                          <svg className="w-5 h-5 mr-3 text-gray-400 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="tracking-wide">{t('myProfile')}</span>
                        </button>

                        {/* DASHBOARD Button for admins */}
                        {isAdmin && (
                          <button
                            onClick={() => window.open('/admin/dashboard', '_blank')}
                            className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-white hover:bg-[#2a2a2c] transition-colors duration-150 group"
                          >
                            <svg className="w-5 h-5 mr-3 text-gray-400 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            <span className="tracking-wide">DASHBOARD</span>
                            <svg className="w-3.5 h-3.5 ml-auto text-gray-500 group-hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </button>
                        )}

                        {/* Divider */}
                        <div className="border-t border-gray-700 my-1"></div>

                        {/* LOGOUT Button */}
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-white hover:bg-[#2a2a2c] transition-colors duration-150 group"
                        >
                          <svg className="w-5 h-5 mr-3 text-gray-400 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          <span className="tracking-wide">{t('logout')}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <button
                    onClick={toggleUserMenu}
                    className="hover:text-cyan-400 text-white focus:outline-none"
                    style={{
                      border: 'solid 1px white',
                      width: '3rem',
                      height: '3rem',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <i className="fa-solid fa-user"></i>
                  </button>
                  
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-black border border-cyan-500 rounded-md shadow-lg py-1 z-50">
                      <div className="px-4 py-2 text-center border-b border-cyan-500/30">
                        <p className="text-sm text-white font-medium">{t('signIn')}</p>
                      </div>
                      <div className="p-2">
                        <div className="mb-2 overflow-hidden">
                          <div className="relative w-full">
                            <button 
                              className="flex items-center justify-center w-full p-2 text-sm font-medium text-gray-700 bg-white rounded hover:bg-gray-100 transition-colors duration-150 cursor-pointer"
                              onClick={() => {
                                // This is a fake button, we're just using it for styling
                                // The actual Google button below this will get clicked
                                document.getElementById('google-signin-button')?.querySelector('div[role="button"]')?.click();
                              }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 48 48" className="mr-2">
                                <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                                <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                                <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                                <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
                              </svg>
                              <span>Google</span>
                            </button>
                            <div className="absolute top-0 left-0 w-full opacity-0">
                              <GoogleAuth />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </nav>
          <div className="md:hidden flex items-center space-x-4">
            {/* Mobile Menu Hamburger */}
            <button 
              className="text-white focus:outline-none" 
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path>
              </svg>
            </button>
          </div>
          
          {/* Mobile Side Navigation */}
          <div className={`mobile-menu-container fixed top-0 right-0 h-full w-64 z-[9999] transform transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`} style={{backgroundColor: '#000000', backdropFilter: 'none', background: '#000000', border: 'none', boxShadow: 'none', opacity: 1}}>
            <div className="mobile-menu-inner w-full h-full bg-black" style={{backgroundColor: '#000000', background: '#000000'}}>
              
              {/* Main Menu */}
              <div className={`mobile-menu-content p-5 flex flex-col h-full ${showMobileDropZones ? 'hidden' : ''}`}>
              <div className="flex justify-end mb-6">
                <button 
                  onClick={toggleMobileMenu} 
                  className="text-white focus:outline-none"
                  aria-label="Close menu"
                >
                  <svg className="w-6 h-6 text-cyan-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <nav className="flex flex-col space-y-6 text-center">
                <a href="/submit" className="text-white text-lg hover:text-cyan-400 transition-colors duration-300">{t('submit')}</a>
                <a href="/gallery" className="text-white text-lg hover:text-cyan-400 transition-colors duration-300">{t('vote')}</a>
                <a href="/shop" className="text-white text-lg hover:text-cyan-400 transition-colors duration-300">{t('shop')}</a>
                
                {/* Drop Zones Button in Mobile Menu */}
                <div className="relative px-4">
                  <button
                    onClick={() => {
                      setShowMobileDropZones(true);
                      // Fetch streamers if not already loaded
                      if (streamers.length === 0) {
                        const fetchStreamers = async () => {
                          try {
                            setLoadingStreamers(true);
                            const response = await fetch('/.netlify/functions/get-admin-ambassadors');
                            const result = await response.json();
                            if (result.success && result.data) {
                              setStreamers(result.data);
                            }
                          } catch (error) {
                            console.error('Error fetching streamers:', error);
                          } finally {
                            setLoadingStreamers(false);
                          }
                        };
                        fetchStreamers();
                      }
                    }}
                    className="drop-zones-btn w-full bg-cyan-600 hover:bg-cyan-700 text-white font-medium px-4 py-3 rounded-lg transition-all duration-300"
                    style={{ backgroundColor: '#0891b2 !important', borderColor: '#0891b2' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0e7490'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0891b2'}
                  >
                    {t('dropZone')}
                  </button>
                </div>
                
                <a href="/application" className="mx-4 bg-gradient-to-r from-pink-600 to-cyan-600 hover:from-pink-700 hover:to-cyan-700 text-white font-medium px-4 py-3 rounded-lg transition-all duration-300 transform hover:scale-105">{t('streamers')}</a>
                
                {/* Mobile Language Selection - Buttons */}
                <div className="flex flex-col space-y-3">
                  <div className="text-gray-400 text-sm font-medium">Langue / Language</div>
                  <div className="flex flex-col gap-2 px-4">
                    <button
                      onClick={() => handleLanguageSelect('fr')}
                      className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all duration-200 ${
                        currentLanguage === 'fr' 
                          ? 'bg-cyan-600 text-white border-2 border-cyan-400' 
                          : 'bg-gray-800 text-gray-300 border border-gray-600 hover:bg-gray-700'
                      }`}
                    >
                      <span className="fi fi-fr" style={{ width: '20px', height: '15px', borderRadius: '2px' }}></span>
                      <span className="font-medium">Français</span>
                      {currentLanguage === 'fr' && <span className="text-cyan-200">✓</span>}
                    </button>
                    <button
                      onClick={() => handleLanguageSelect('en')}
                      className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all duration-200 ${
                        currentLanguage === 'en' 
                          ? 'bg-cyan-600 text-white border-2 border-cyan-400' 
                          : 'bg-gray-800 text-gray-300 border border-gray-600 hover:bg-gray-700'
                      }`}
                    >
                      <span className="fi fi-us" style={{ width: '20px', height: '15px', borderRadius: '2px' }}></span>
                      <span className="font-medium">English</span>
                      {currentLanguage === 'en' && <span className="text-cyan-200">✓</span>}
                    </button>
                  </div>
                </div>
              </nav>
              
              {/* Mobile Login/User Section */}
              <div className="mt-8 flex flex-col items-center w-full">
                {isAuthenticated ? (
                  <div className="flex flex-col items-center w-full px-4">
                    <img 
                      src={user.picture} 
                      alt={user.name} 
                      className="w-12 h-12 rounded-full border-2 border-cyan-500 mb-2"
                    />
                    <p className="text-white font-medium">{user.name}</p>
                    <p className="text-gray-400 text-sm mb-3">{user.email}</p>
                    <button
                      onClick={handleLogout}
                      className="w-full py-3 px-4 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition duration-300"
                    >
                      {t('signOut')}
                    </button>
                  </div>
                ) : (
                  <div className="w-full px-4">
                    <p className="text-white font-medium mb-3 text-center">{t('signIn')}</p>
                    {/* Copy exact desktop design */}
                    <div className="mb-2 overflow-hidden">
                      <div className="relative w-full">
                        <button 
                          className="flex items-center justify-center w-full py-3 px-4 text-sm font-medium text-gray-700 bg-white rounded-lg hover:bg-gray-100 transition-colors duration-150 cursor-pointer border border-gray-300"
                          style={{ backgroundColor: 'white' }}
                          onClick={() => {
                            // Find and click the actual Google button
                            const allButtons = document.querySelectorAll('[id^="google-signin-button"]');
                            for (const btn of allButtons) {
                              const googleBtn = btn.querySelector('div[role="button"]');
                              if (googleBtn) {
                                googleBtn.click();
                                return;
                              }
                            }
                          }}
                          disabled={googleLoginLoading}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 48 48" className="mr-2">
                            <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                            <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                            <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                            <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
                          </svg>
                          <span>Google</span>
                        </button>
                        {/* Loading overlay */}
                        {googleLoginLoading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-700 bg-opacity-80 rounded z-10">
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-cyan-500 border-t-transparent"></div>
                          </div>
                        )}
                        {/* Hidden Google button */}
                        <div className="absolute top-0 left-0 w-full opacity-0">
                          <GoogleAuth />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-auto mb-10 flex justify-center">
                <div className="bg-black bg-opacity-50 py-2 px-4 rounded-full border border-cyan-500">
                  <span className="text-cyan-500 font-medium flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    {t('nextDrop')}: 38 {t('days')}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Drop Zones Content */}
            <div 
              className={`mobile-dropzones-content p-5 flex flex-col h-full ${showMobileDropZones ? '' : 'hidden'}`}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex justify-end mb-6">
                <button 
                  onClick={() => {
                    setShowMobileDropZones(false);
                    setShowDropZoneDropdown(false); // Close desktop dropdown too
                  }} 
                  className="text-white focus:outline-none"
                  aria-label="Back to menu"
                >
                  <svg className="w-6 h-6 text-cyan-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <h3 className="text-white text-xl font-bold mb-4">{t('dropZone')}</h3>
              
              {/* Search Field */}
              <input
                type="text"
                placeholder={t('searchStreamers')}
                value={streamerSearchQuery}
                onChange={(e) => setStreamerSearchQuery(e.target.value)}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 mb-4"
              />
              
              {/* Suggestions Label */}
              <div className="text-gray-400 text-sm font-medium mb-3">{t('suggestions')}</div>
              
              {/* Streamers Grid */}
              <div className="flex-1 overflow-y-auto">
                {loadingStreamers ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-cyan-500 border-t-transparent"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {streamers
                      .filter(streamer => {
                        if (!streamerSearchQuery.trim()) return true;
                        const searchLower = streamerSearchQuery.toLowerCase().trim();
                        const ownerMatch = streamer.owner?.toLowerCase().includes(searchLower);
                        const nameMatch = streamer.name?.toLowerCase().includes(searchLower);
                        return ownerMatch || nameMatch;
                      })
                      .map((streamer) => {
                        const defaultImage = 'https://res.cloudinary.com/dez2cotie/image/upload/v1751233643/pngegg_pk3dvl.png';
                        let imageUrl = defaultImage;
                        
                        try {
                          if (streamer.image) {
                            if (typeof streamer.image === 'string' && streamer.image.startsWith('{')) {
                              const imageData = JSON.parse(streamer.image);
                              if (imageData.logo && Array.isArray(imageData.logo) && imageData.logo.length > 0) {
                                imageUrl = imageData.logo[0];
                              } else if (imageData.profile && Array.isArray(imageData.profile) && imageData.profile.length > 0) {
                                imageUrl = imageData.profile[0];
                              }
                            }
                            else if (typeof streamer.image === 'string' && streamer.image.startsWith('[')) {
                              const imageUrls = JSON.parse(streamer.image);
                              if (imageUrls.length > 0) {
                                imageUrl = imageUrls[0];
                              }
                            }
                            else if (Array.isArray(streamer.image) && streamer.image.length > 0) {
                              imageUrl = streamer.image[0];
                            }
                            else if (typeof streamer.image === 'string' && streamer.image.length > 0) {
                              imageUrl = streamer.image;
                            }
                          }
                        } catch (error) {
                          console.error('Error parsing streamer image:', error, streamer);
                        }
                        
                        return (
                          <a
                            key={streamer.id}
                            href={`/${streamer.owner}`}
                            className="flex flex-col items-center group"
                            onClick={() => {
                              setShowMobileDropZones(false);
                              setShowDropZoneDropdown(false); // Close desktop dropdown too
                              toggleMobileMenu();
                            }}
                          >
                            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-gray-600 group-hover:border-cyan-500 transition-colors mb-2">
                              <img 
                                src={imageUrl} 
                                alt={streamer.owner || streamer.name}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.src = defaultImage; }}
                              />
                            </div>
                            <p className="text-white text-xs text-center font-medium truncate w-full">{streamer.owner}</p>
                          </a>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
            </div>
          </div>
          
          {/* Overlay when mobile menu is open */}
          {mobileMenuOpen && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-[55]" 
              onClick={toggleMobileMenu}
              aria-hidden="true"
            ></div>
          )}
        </div>
      </div>
    </header>
    
    {/* Success Notification */}
    {submitSuccess && (
      <div className="fixed bottom-5 right-5 z-50 bg-green-600 text-white p-4 rounded-md shadow-lg flex items-center transition-all duration-300 ease-in-out">
        <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <span>{successMessage}</span>
        <button 
          onClick={() => setSubmitSuccess(false)} 
          className="ml-4 text-white hover:text-gray-200 focus:outline-none"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    )}
    
    {/* Challenge Modal */}
    {showChallengeModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
        <div ref={modalRef} className="bg-[#2D3748] p-8 rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <button 
            onClick={closeChallengeModal}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-150 text-2xl"
            aria-label="Close dialog"
          >
            ×
          </button>
          
          <h2 className="text-3xl font-bold mb-8 text-yellow-400">Create a new Challenge</h2>
          
          <form onSubmit={handleChallengeSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Basic Info */}
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-cyan-400 mb-2">
                    Name of the challenge
                  </label>
                  <input 
                    name="name"
                    type="text" 
                    value={challengeForm.name}
                    onChange={handleChallengeFormChange}
                    placeholder="Ex: Ajoli vs Pasta"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent" 
                    required
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-cyan-400 mb-2">
                      Start date
                    </label>
                    <input 
                      name="startDate"
                      type="date" 
                      value={challengeForm.startDate}
                      onChange={handleChallengeFormChange}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent" 
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-cyan-400 mb-2">
                      End date
                    </label>
                    <input 
                      name="endDate"
                      type="date" 
                      value={challengeForm.endDate}
                      onChange={handleChallengeFormChange}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent" 
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-cyan-400 mb-2">
                    Thematic
                  </label>
                  <select 
                    name="thematic"
                    value={challengeForm.thematic}
                    onChange={handleChallengeFormChange}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    disabled={isSubmitting}
                  >
                    <option value="Food">Food</option>
                    <option value="Sport">Sport</option>
                    <option value="Art">Art</option>
                    <option value="Gaming">Gaming</option>
                    <option value="Music">Music</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-cyan-400 mb-2">
                    Description of the challenge
                  </label>
                  <textarea 
                    name="description"
                    value={challengeForm.description}
                    onChange={handleChallengeFormChange}
                    placeholder="Describe the challenge in a few words..."
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none h-32" 
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              
              {/* Right Column - Awards */}
              <div>
                <h3 className="text-cyan-400 font-semibold mb-4">Awards</h3>
                
                <div className="space-y-4">
                  {/* 1st Place Rewards */}
                  <div className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                        <span className="text-black font-bold text-sm">1</span>
                      </div>
                      <span className="text-white font-medium">1ère place</span>
                    </div>
                    
                    <div className="space-y-2">
                      {challengeForm.rewards.first.map((reward, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            placeholder={`Reward ${index + 1}`}
                            className="flex-1 bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                            value={reward}
                            onChange={(e) => handleRewardChange('first', index, e.target.value)}
                            disabled={isSubmitting}
                          />
                          {challengeForm.rewards.first.length > 2 && (
                            <button 
                              type="button"
                              className="text-red-400 hover:text-red-300 text-lg px-2"
                              onClick={() => removeReward('first', index)}
                              disabled={isSubmitting}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      <button 
                        type="button"
                        className="text-cyan-400 text-sm hover:text-cyan-300 transition-colors" 
                        onClick={() => addReward('first')}
                        disabled={isSubmitting}
                      >
                        + Add a reward
                      </button>
                    </div>
                  </div>
                  
                  {/* 2nd Place Rewards */}
                  <div className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center">
                        <span className="text-black font-bold text-sm">2</span>
                      </div>
                      <span className="text-white font-medium">2nd place</span>
                    </div>
                    
                    <div className="space-y-2">
                      {challengeForm.rewards.second.map((reward, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            placeholder={`Reward ${index + 1}`}
                            className="flex-1 bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                            value={reward}
                            onChange={(e) => handleRewardChange('second', index, e.target.value)}
                            disabled={isSubmitting}
                          />
                          {challengeForm.rewards.second.length > 2 && (
                            <button 
                              type="button"
                              className="text-red-400 hover:text-red-300 text-lg px-2"
                              onClick={() => removeReward('second', index)}
                              disabled={isSubmitting}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      <button 
                        type="button"
                        className="text-cyan-400 text-sm hover:text-cyan-300 transition-colors" 
                        onClick={() => addReward('second')}
                        disabled={isSubmitting}
                      >
                        + Add a reward
                      </button>
                    </div>
                  </div>
                  
                  {/* 3rd Place Rewards */}
                  <div className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center">
                        <span className="text-black font-bold text-sm">3</span>
                      </div>
                      <span className="text-white font-medium">3ème place</span>
                    </div>
                    
                    <div className="space-y-2">
                      {challengeForm.rewards.third.map((reward, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            placeholder={`Reward ${index + 1}`}
                            className="flex-1 bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                            value={reward}
                            onChange={(e) => handleRewardChange('third', index, e.target.value)}
                            disabled={isSubmitting}
                          />
                          {challengeForm.rewards.third.length > 2 && (
                            <button 
                              type="button"
                              className="text-red-400 hover:text-red-300 text-lg px-2"
                              onClick={() => removeReward('third', index)}
                              disabled={isSubmitting}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      <button 
                        type="button"
                        className="text-cyan-400 text-sm hover:text-cyan-300 transition-colors" 
                        onClick={() => addReward('third')}
                        disabled={isSubmitting}
                      >
                        + Add a reward
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {submitError && (
              <div className="mt-6 bg-red-900 border border-red-700 text-red-200 p-4 rounded-lg">
                {submitError}
              </div>
            )}
            
            <div className="flex justify-center space-x-4 mt-8">
              <button 
                type="submit" 
                disabled={isSubmitting}
                className="bg-pink-600 hover:bg-pink-700 text-white px-8 py-3 rounded-lg text-lg font-semibold transition-colors shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : 'Create the challenge'}
              </button>
              <button 
                type="button" 
                onClick={closeChallengeModal}
                disabled={isSubmitting}
                className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-3 rounded-lg text-lg font-semibold transition-colors shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  );
}

export default Header;
